import React, { useMemo } from 'react'
import { css } from '@emotion/core'
import { Tabs, Tab, useContainerWidth } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useShowTemple } from '@fs/zion-permissions'
import useDSApplicationMode from '../../../../hooks/useDSApplicationMode'
import PersonMemories from './PersonMemories/PersonMemories'
import PersonOrdinances from './PersonOrdinances/PersonOrdinances'
import PersonAbout from './PersonAbout/PersonAbout'
import { PersonType } from '../../../../propTypes/personDataProps'
import { usePersonViewerState } from '../../state/PersonViewerStore'

const TabList = ({ activeTab, setActiveTab, showTemple }) => {
  const [t] = useTranslation()
  const [selectedTab, setSelectedTab] = React.useState(0)

  const tabsData = useMemo(() => {
    const tabs = [
      { id: 'About', title: t('person-details.about', 'About') },
      { id: 'Memories', title: t('person-details.memories', 'Memories') },
    ]
    if (showTemple) {
      tabs.push({ id: 'Ordinances', title: t('person-details.ordinances', 'Ordinances') })
    }
    return tabs
  }, [t, showTemple])

  React.useEffect(() => {
    const currentTab = tabsData.map((tab) => tab.id).indexOf(activeTab.id)
    setSelectedTab(currentTab)
  }, [activeTab.id, tabsData])

  return (
    <Tabs
      index={selectedTab}
      elevated={false}
      onChange={(index) => {
        setActiveTab(tabsData[index])
        setSelectedTab(index)
      }}
      fullWidth
    >
      {tabsData.map((tabData) => (
        <Tab
          data-testid="person-tabs-tab"
          key={tabData?.id}
          linkName={`PersonTabs:${tabData?.id}:CLICK`}
          title={tabData?.title}
        />
      ))}
    </Tabs>
  )
}

const TabContent = ({ isAccessible, personData, showTemple, activeTab }) => {
  const personViewerState = usePersonViewerState()
  const appMode = useDSApplicationMode()
  const memoryProps = {
    ...personData,
    isAccessible,
    centerOrStation: appMode === 'STATION',
    treePerson: { id: personData.personId, ...personData },
  }
  const tabContentCSS = css`
    overflow-y: ${personViewerState.tabContentOverflow};
    margin-top: 10px;
    -webkit-overflow-scrolling: touch;
    flex-grow: 1;
  `
  return (
    <div css={tabContentCSS}>
      {activeTab.id === 'About' && <PersonAbout personData={personData} />}
      {activeTab.id === 'Memories' && <PersonMemories {...memoryProps} />}
      {showTemple && activeTab.id === 'Ordinances' && <PersonOrdinances personId={personData.personId} />}
    </div>
  )
}

export default function PersonTabs({ isAccessible, personData }) {
  const [showTemple] = useShowTemple()
  const atWidth = useContainerWidth()
  const [t] = useTranslation()
  const initialActiveTab = { id: 'About', title: t('person-details.about', 'About') }
  const [activeTab, setActiveTab] = React.useState(initialActiveTab)
  const listProps = {
    isAccessible,
    personData,
    activeTab,
    setActiveTab,
    showTemple,
  }

  const contentProps = {
    ...listProps,
  }

  const largeContainerCSS = css`
    z-index: 1;
    width: 100%;
    max-width: 800px;
    align-self: center;
    ${
      isAccessible &&
      `
        position: absolute;
        bottom: 60px;
    `
    }
  `

  return (
    <>
      <div css={atWidth({ default: null, lg: largeContainerCSS })}>
        <TabList {...listProps} />
      </div>
      <TabContent {...contentProps} />
    </>
  )
}

PersonTabs.propTypes = {
  personData: PersonType,
}
